import { Spinner } from "@shapr3d/frontend-lib";
import classNames from "classnames";
import type React from "react";

import { usePriceConfig } from "../../../hooks/usePriceConfig";
import useTranslation from "../../../hooks/useTranslation";
import { SubscriptionPeriod, SubscriptionTier } from "../../../types";
import { BulletPoint } from "./BulletPoint";

import style from "./InvitePromo.module.css";

interface InvitePromoProps {
  type: "contributor" | "editor";
  period?: SubscriptionPeriod;
}

export const InvitePromo: React.FC<InvitePromoProps> = ({ type, period }) => {
  const { data: priceConfigData, isLoading: isPriceConfigLoading } = usePriceConfig();
  const studioTierConfig = priceConfigData?.tiers[SubscriptionTier.Studio];
  const liteTierConfig = priceConfigData?.tiers[SubscriptionTier.Lite];
  const tierConfig = liteTierConfig ?? studioTierConfig;
  const { t, m } = useTranslation();

  return (
    <div className={style.contributorPromoContainer}>
      {type === "editor" && (
        <>
          {isPriceConfigLoading && (
            <div className={style.title}>
              &nbsp;
              <Spinner size={18} />
            </div>
          )}
          {!isPriceConfigLoading && (
            <span className={style.title}>
              {t(m.MembersPage.PricePerSeat, {
                price: `${priceConfigData?.currencySymbol}${period === SubscriptionPeriod.Monthly ? tierConfig?.monthly : tierConfig?.yearly}`,
              })}
            </span>
          )}
          <span className={classNames(style.subtitle, style.blue)}>
            {t(m.MembersPage.EditorsDescription, {
              mark: (chunks: React.ReactNode) => <mark>{chunks}</mark>,
            })}
          </span>
          <div className={style.benefits}>
            <BulletPoint icon="checkmark_circle_filled" text={t(m.MembersPage.EditProjects)} />
            <BulletPoint icon="checkmark_circle_filled" text={t(m.MembersPage.CreateNewProjects)} />
            <BulletPoint icon="checkmark_circle_filled" text={t(m.MembersPage.ExportProjects)} />
            <BulletPoint icon="checkmark_circle_filled" text={t(m.MembersPage.DraftProjects)} />
          </div>
        </>
      )}
      {type === "contributor" && (
        <>
          <span className={style.title}>{t(m.MembersPage.Free)}</span>
          <span className={classNames(style.subtitle, style.yellow)}>
            {t(m.MembersPage.ContributorsDescription, {
              mark: (chunks: React.ReactNode) => <mark>{chunks}</mark>,
            })}
          </span>
          <div className={style.benefits}>
            <BulletPoint icon="checkmark_circle_filled" text={t(m.MembersPage.EditProjects)} />
            <BulletPoint icon="checkmark_circle" text={t(m.MembersPage.UpToTwoProjects)} />
            <BulletPoint icon="close_circle_filled" text={t(m.MembersPage.ExportProjects)} />
            <BulletPoint icon="close_circle_filled" text={t(m.MembersPage.DraftProjects)} />
          </div>
        </>
      )}
    </div>
  );
};
